import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../context/CartContext';
import { useUser } from '../context/UserContext';
import { useToast } from '../hooks/use-toast';
import { getProduct, createOrder } from '../services/api';
import usePageSEO from '../hooks/usePageSEO';

const CartPage = () => {
  const { t, i18n } = useTranslation();
  const { cart, updateQuantity, removeFromCart, clearCart, loading } = useCart();
  const { user, isAuthenticated } = useUser();
  const { toast } = useToast();
  usePageSEO({ title: t('pages.cartTitle'), description: t('pages.cartDesc'), noIndex: true });
  const [products, setProducts] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderComplete, setOrderComplete] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ name: '', email: '', phone: '', address: '' });
  const [notes, setNotes] = useState('');
  const lang = i18n.language?.substring(0, 2) || 'en';
  const isLoggedIn = isAuthenticated && user;

  useEffect(() => {
    const loadProducts = async () => {
      setLoadingProducts(true);
      const productMap = {};
      for (const item of cart.items) {
        try {
          const product = await getProduct(item.product_id);
          productMap[item.product_id] = product;
        } catch (e) {
          console.error('Failed to load product:', item.product_id);
        }
      }
      setProducts(productMap);
      setLoadingProducts(false);
    };
    if (cart.items.length > 0) loadProducts();
    else setLoadingProducts(false);
  }, [cart.items]);

  useEffect(() => {
    if (!isLoggedIn) return;
    setCustomerInfo(prev => ({
      ...prev,
      name: user.name || prev.name,
      email: user.email || prev.email,
      phone: user.phone || prev.phone
    }));
  }, [isLoggedIn, user]);

  const subtotal = cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleCheckout = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const orderData = {
        customer: customerInfo,
        items: cart.items.map(item => ({
          product_id: item.product_id,
          product_name: products[item.product_id]?.name?.[lang] || item.product_id,
          quantity: item.quantity,
          price: item.price
        })),
        notes
      };
      const order = await createOrder(orderData);
      setOrderComplete(order);
      await clearCart();
    } catch (error) {
      toast({ title: "Error", description: "Failed to place order", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (orderComplete) {
    return (
      <div className="min-h-screen pt-24 pb-16" data-testid="order-confirmation">
        <div className="container mx-auto px-4 max-w-lg text-center py-16">
          <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('pages.orderPlaced')}</h1>
          <p className="text-gray-600 mb-2">{t('pages.orderNumberLabel')}</p>
          <p className="text-2xl font-bold text-red-600 mb-6" data-testid="order-number">{orderComplete.order_number}</p>
          <p className="text-gray-600 mb-8">{t('pages.orderThankYou')}</p>
          <div className="flex gap-4 justify-center">
            <Button className="bg-red-600 hover:bg-red-700" asChild><Link to="/shop">{t('pages.continueShopping')}</Link></Button>
            <Button variant="outline" asChild><Link to="/">{t('pages.backToHome')}</Link></Button>
          </div>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0 && !loadingProducts) {
    return (
      <div className="min-h-screen pt-24 pb-16">
        <div className="container mx-auto px-4 text-center py-16">
          <ShoppingBag className="h-20 w-20 text-gray-300 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('pages.emptyCart')}</h1>
          <p className="text-gray-600 mb-8">{t('pages.emptyCartDesc')}</p>
          <Button className="bg-red-600 hover:bg-red-700" asChild><Link to="/shop">{t('pages.browseProducts')}</Link></Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="cart-page">
      <div className="container mx-auto px-4">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/shop" className="text-gray-500 hover:text-red-600"><ArrowLeft className="h-5 w-5" /></Link>
          <h1 className="text-3xl font-bold text-gray-900">{t('pages.cartTitle')}</h1>
          <span className="text-gray-500">({cart.items.length} {cart.items.length !== 1 ? t('pages.items') : t('pages.item')})</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item) => {
              const product = products[item.product_id];
              return (
                <Card key={item.product_id} data-testid={`cart-item-${item.product_id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    {product && <img src={product.image_url} alt={product.name[lang]} className="w-24 h-24 object-cover rounded" />}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 truncate">{product?.name?.[lang] || '...'}</h3>
                      <p className="text-sm text-gray-500">{product?.category?.[lang]}</p>
                      <p className="text-red-600 font-bold mt-1">{item.price.toFixed(3)} BHD</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => updateQuantity(item.product_id, item.quantity - 1)} disabled={loading}><Minus className="h-4 w-4" /></Button>
                      <span className="w-8 text-center font-semibold">{item.quantity}</span>
                      <Button size="sm" variant="outline" onClick={() => updateQuantity(item.product_id, item.quantity + 1)} disabled={loading}><Plus className="h-4 w-4" /></Button>
                    </div>
                    <div className="text-right min-w-[100px]">
                      <p className="font-bold text-gray-900">{(item.price * item.quantity).toFixed(3)} BHD</p>
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => removeFromCart(item.product_id)} disabled={loading}><Trash2 className="h-4 w-4" /></Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div>
            <Card className="sticky top-24">
              <CardHeader><CardTitle>{t('pages.orderSummary')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between text-gray-600"><span>{t('pages.subtotal')}</span><span>{subtotal.toFixed(3)} BHD</span></div>
                <div className="border-t pt-4 flex justify-between text-lg font-bold"><span>{t('pages.total')}</span><span className="text-red-600" data-testid="cart-total">{subtotal.toFixed(3)} BHD</span></div>

                {!showCheckout ? (
                  <Button className="w-full bg-red-600 hover:bg-red-700" onClick={() => setShowCheckout(true)} data-testid="checkout-btn">{t('pages.proceedCheckout')}</Button>
                ) : (
                  <form onSubmit={handleCheckout} className="space-y-3 border-t pt-4">
                    <h3 className="font-bold">{t('pages.contactDetails')}</h3>
                    {isLoggedIn ? (
                      <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-600">
                        <p className="font-medium text-gray-900">{t('pages.usingAccountDetails')}</p>
                        <p>{customerInfo.name}</p>
                        <p>{customerInfo.email}</p>
                        <p>{customerInfo.phone}</p>
                      </div>
                    ) : (
                      <>
                        <div><Label>{t('pages.fullName')} *</Label><Input data-testid="checkout-name" value={customerInfo.name} onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })} required /></div>
                        <div><Label>{t('pages.email')} *</Label><Input data-testid="checkout-email" type="email" value={customerInfo.email} onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })} required /></div>
                        <div><Label>{t('pages.phone')} *</Label><Input data-testid="checkout-phone" value={customerInfo.phone} onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })} required /></div>
                      </>
                    )}
                    <div><Label>{t('pages.deliveryAddress')} *</Label><Textarea data-testid="checkout-address" value={customerInfo.address} onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })} rows={2} required /></div>
                    <div><Label>{t('pages.notes')}</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder={t('pages.specialInstructions')} /></div>
                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={submitting} data-testid="place-order-btn">
                      {submitting ? t('pages.placingOrder') : t('pages.placeOrder')}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
